import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, track } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSettings } from "@/context/SettingsContext";

export default function ProductPage() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    api.get(`/products/${slug}`).then(({ data }) => {
      setProduct(data);
      track("product_view", { slug });
    }).catch(() => {});
  }, [slug]);

  if (!product) return <div className="min-h-[60vh] flex items-center justify-center text-forest">Loading…</div>;

  const compliance = settings?.compliance || {};

  const Section = ({ title, children, testid }) => (
    <div className="py-6 border-b border-forest/10" data-testid={testid}>
      <h3 className="font-serif text-xl font-semibold text-forest-dark mb-2">{title}</h3>
      <div className="text-charcoal/75 text-sm leading-relaxed">{children}</div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <nav className="text-sm text-charcoal/50 mb-6" aria-label="Breadcrumb">
        <a href="/" className="hover:text-forest">Home</a> <span className="mx-1">/</span>
        <span className="text-charcoal">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="rounded-2xl overflow-hidden aspect-square bg-sand shadow-lg">
            <img src={product.images?.[active]} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
          </div>
          <div className="flex gap-3 mt-4">
            {(product.images || []).map((img, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                className={`h-20 w-20 rounded-xl overflow-hidden border-2 ${active === i ? "border-wheat" : "border-transparent"}`}>
                <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>

        {/* Buy area */}
        <div>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-forest-dark">{product.name}</h1>
          <p className="text-charcoal/70 mt-3">{product.short_description}</p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {product.variants.filter((v) => v.active !== false).map((v) => (
              <ProductCard key={v.sku} product={product} variant={v} />
            ))}
          </div>
        </div>
      </div>

      {/* Details */}
      <div className="mt-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <Section title="About the Product" testid="section-about">{product.about}</Section>
          <Section title="Ingredients" testid="section-ingredients">
            <p className="mb-3 text-charcoal/60 italic">{product.formulation_note}</p>
            <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {product.ingredients?.map((ing) => (
                <li key={ing.name} className="flex justify-between bg-sand rounded-lg px-3 py-2">
                  <span>{ing.name}</span><span className="font-mono text-forest font-semibold">{ing.percent}%</span>
                </li>
              ))}
            </ul>
          </Section>
          <Section title="How It Is Made" testid="section-how-made">{product.how_made}</Section>
          <Section title="Storage" testid="section-storage">{product.storage}</Section>
          <Section title="Allergen Information" testid="section-allergen">{product.allergen}</Section>
          <Section title="Nutrition Information" testid="section-nutrition">
            <span className="italic">{product.nutrition_note}</span>
          </Section>

          <div className="py-6">
            <h3 className="font-serif text-xl font-semibold text-forest-dark mb-3">FAQs</h3>
            <Accordion type="single" collapsible className="w-full" data-testid="product-faqs">
              {product.faqs?.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`}>
                  <AccordionTrigger className="text-left text-sm font-semibold" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-charcoal/70 text-sm">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <div className="rounded-2xl bg-forest-950 text-cream p-6 sticky top-24">
            <h4 className="font-serif text-lg text-wheat mb-4">Compliance & Info</h4>
            <ul className="space-y-3 text-sm text-cream/80">
              <li>{compliance.fssai}</li>
              <li>{compliance.gstin}</li>
              <li>{compliance.udyam}</li>
              <li>Marketed by: Areeba, {settings?.contact?.address}</li>
              <li>Customer Care: {compliance.customer_care}</li>
              <li className="text-cream/50 text-xs pt-2 border-t border-white/10">MRP is printed on the pack. Nutrition values will be updated after verified laboratory analysis.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
